/**
 * 20261028 학생 송출 owner·종료 작업 판단 (web-relay R-224, milestone_42 §3.14·§3.16·§3.18)
 *
 * 왜 있나: Home 학생이 [끝내기]로 /main에 있는 동안 송출 복구 감시가 송출을 계속 다시 세웠다(R-224).
 *          종료 표시는 첫 await 전에 하고, 정리가 끝나기 전에는 새 handsup 시작을 보류한다.
 *
 * 상태: idle(연 적 없음, 기존 동작) → active(유효 lSeq/uSeq로 연 owner) → exiting(종료 작업 중) → exited
 * 판단만 한다 — 전역도 import 도 없다. 실제 정리·요청은 주입된 콜백이 한다(sendRecreateWatch.js와 같은 형식).
 */

export const SEND_EXIT_RESULT = Object.freeze({
    DONE         : "done",
    RELAY_UNKNOWN: "relay-unknown",
    ERROR        : "error"
});

// 수신 입구에서 판정하는 handsup 송출 응답(서버가 envelope에 liveSeq를 싣는다)
const SEND_RESPONSE_ACTIONS = [
    "live_createProducerTransport",
    "live_connectProducerTransport",
    "live_produce",
    "live_restartProducerTransport",
    "live_unpublish"
];

const toSeq = (value) => (value === undefined || value === null || value === "") ? null : Number(value);

/**
 * 보류한 시작이 아직 유효한지 현재 store 상태로 확인한다(R-236).
 *
 * @param {Object} state - store.getState()
 * @param {Object} req   - { lSeq, uSeq, elements: [Element] }
 * @returns {Boolean}
 */
export const isStartIntentValid = (state, req) => {
    try {
        const live = state && state.live ? state.live.performLiveInfo : null;
        const user = state ? state.user : null;
        if (!live || !user) return false;
        if (toSeq(live.liveSeq) !== toSeq(req.lSeq) || toSeq(req.lSeq) === null || toSeq(req.lSeq) < 0) return false;
        if (live.liveStatus !== "Y" || live.handsUpYN !== "Y") return false;
        if (toSeq(req.uSeq) === null || toSeq(req.uSeq) < 0 || toSeq(user.userSeq) !== toSeq(req.uSeq)) return false; // 20261028 로그아웃 번호로 보류 시작하지 않음 (R-244)
        // 20261028 보류한 수업 화면의 필수 DOM이 사라졌으면 다시 송출하지 않는다 (R-244).
        const elements = req.elements;
        if (!Array.isArray(elements) || elements.length === 0) return false;
        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            if (el === undefined || el === null || el.isConnected !== true) return false;
        }
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * @param {Object}   [deps]
 * @param {Function} [deps.log] - 로그 출력(기본 console.log)
 */
export const createSendExitGuard = (deps = {}) => {
    const log = deps.log || ((msg) => console.log(msg));

    let state = "idle";
    let epoch = 0;
    let owner = null;           // { liveSeq, userSeq }
    let job = null;             // { token, promise }
    let pendingStart = null;    // { run, isValid }
    // 20261030 브라우저 획득 요청 대기 ticket (web-relay R-238·R-240, milestone_42 §3.37·§3.39)
    const acquires = new Map(); // id → { ticket, done:Promise, resolve }
    let acquireSeq = 0;

    const cancelPending = (why) => {
        if (pendingStart !== null) {
            log(`sendExitGuard - 보류한 시작을 취소한다(${why}).`);
            pendingStart = null;
        }
    };

    const runPending = () => {
        const p = pendingStart;
        pendingStart = null;
        if (p === null) return;

        let isValid = false;
        try {
            isValid = p.isValid() === true;
        } catch (err) {
            isValid = false;
        }

        if (!isValid) {
            log("sendExitGuard - 보류한 시작이 현재 수업/사용자/화면과 맞지 않는다. 버린다.");
            return;
        }

        log("sendExitGuard - 정리가 끝났다. 보류한 시작을 1회 실행한다.");
        try {
            p.run();
        } catch (err) {
            log(`sendExitGuard - 보류한 시작 실행 오류 ${err && err.message}`);
        }
    };

    const finishOnce = (token, fn) => {
        if (!token) return Promise.resolve([]);
        if (!token.finishPromise) {
            token.finishPromise = Promise.resolve()
                .then(() => fn(token))
                .then((failures) => Array.isArray(failures) ? failures : [])
                .catch((err) => [{ name: "finish", err }]);
        }
        return token.finishPromise;
    };

    return {
        getState: () => state,
        getEpoch: () => epoch,
        getOwner: () => owner,
        isEpoch: (value) => value === epoch,
        isExitingToken: (token) => state === "exiting" && job !== null && job.token === token,

        /** exiting/exited 에서만 막는다. idle 은 기존 동작 그대로 둔다. */
        canRequestSend: () => state !== "exiting" && state !== "exited",

        /**
         * 유효한 새 handsup 시작에서 owner 를 연다. 같은 owner 가 active 면 그대로 둔다.
         * @returns {Boolean} 열렸거나 유지되면 true
         */
        openOwner: (liveSeq, userSeq) => {
            const lSeq = toSeq(liveSeq);
            const uSeq = toSeq(userSeq);
            if (lSeq === null || uSeq === null || lSeq < 0 || uSeq < 0) {
                log(`sendExitGuard - 유효하지 않은 시작이라 owner 를 열지 않는다. liveSeq[${liveSeq}], userSeq[${userSeq}], state[${state}]`);
                return false;
            }
            if (state === "exiting") {
                log("sendExitGuard - 종료 작업 중이라 owner 를 열지 않는다.");
                return false;
            }
            if (state === "active" && owner !== null && owner.liveSeq === lSeq && owner.userSeq === uSeq) {
                return true;
            }
            epoch++;
            state = "active";
            owner = { liveSeq: lSeq, userSeq: uSeq };
            log(`sendExitGuard - owner 를 연다. liveSeq[${lSeq}], userSeq[${uSeq}], epoch[${epoch}]`);
            return true;
        },

        /**
         * 종료 작업 중이면 시작을 보류한다(마지막 1건만). 보류했으면 true.
         */
        deferStart: (run, isValid) => {
            if (state !== "exiting") return false;
            if (pendingStart !== null) log("sendExitGuard - 앞서 보류한 시작을 새 시작으로 바꾼다.");
            else log("sendExitGuard - 종료 작업 중이라 시작을 보류한다.");
            pendingStart = { run, isValid };
            return true;
        },

        /**
         * 종료 작업. onBegin 은 동기로 첫 await 전에 끝난다. 같은 종료가 다시 오면 같은 Promise 를 돌려준다.
         *
         * @param {String}   why
         * @param {Object}   hooks
         * @param {Function} hooks.onBegin   - (token) 동기: 종료 표시 뒤 감시/재시작 중단
         * @param {Function} hooks.onFinish  - (token) → failures[]: 자원 그룹 정리
         * @param {Function} hooks.body      - (token) → Promise: 기존 종료 본문
         * @param {Function} [hooks.waitAcquires] - (token) → Promise: 진행 중 획득 완료 대기(2단계)
         * @param {Function} [hooks.onSettled] - (token) → failures[]: 보류 시작 직전 동기 마무리(20261029 R-259)
         * @returns {Promise<String>} SEND_EXIT_RESULT
         */
        runExit: (why, hooks) => {
            if (state === "exiting" && job !== null) {
                cancelPending(`같은 종료 재호출: ${why}`);
                log(`sendExitGuard - 이미 종료 작업 중이다. 같은 작업을 돌려준다(${why}).`);
                return job.promise;
            }

            cancelPending(`종료 시작: ${why}`);
            epoch++;
            state = "exiting";
            const token = {
                epoch,
                why,
                liveSeq: owner !== null ? owner.liveSeq : null,
                userSeq: owner !== null ? owner.userSeq : null,
                relayUnknown: false,
                finishPromise: null
            };
            log(`sendExitGuard - 종료 작업 시작(${why}). epoch[${epoch}], liveSeq[${token.liveSeq}]`);

            // 본문이 첫 await 전에 같은 종료를 다시 불러도 같은 작업을 받도록 job 을 먼저 세운다.
            let resolveJob = null;
            const jobPromise = new Promise((resolve) => { resolveJob = resolve; });
            job = { token, promise: jobPromise };

            const failures = [];
            try {
                hooks.onBegin(token);
            } catch (err) {
                failures.push({ name: "begin", err });
            }

            (async () => {
                let isBodyError = false;
                try {
                    await hooks.body(token);
                } catch (err) {
                    isBodyError = true;
                    failures.push({ name: "body", err });
                    log(`sendExitGuard - 종료 본문 오류(${why}) ${err && err.message}`);
                } finally {
                    const finishFailures = await finishOnce(token, hooks.onFinish);
                    finishFailures.forEach((f) => failures.push(f));
                    if (typeof hooks.waitAcquires === "function") {
                        try {
                            await hooks.waitAcquires(token);
                        } catch (err) {
                            failures.push({ name: "acquire", err });
                        }
                    }
                }

                // 20261029 스냅샷 닫기와 exited 전이 사이에 await 없이 후속 입력·실패를 보호한다 (R-259).
                if (typeof hooks.onSettled === "function") {
                    try {
                        const settledFailures = hooks.onSettled(token);
                        if (Array.isArray(settledFailures)) settledFailures.forEach((f) => failures.push(f));
                    } catch (err) {
                        failures.push({ name: "settled", err });
                    }
                }

                let result = SEND_EXIT_RESULT.DONE;
                if (isBodyError || failures.length > 0) result = SEND_EXIT_RESULT.ERROR;
                else if (token.relayUnknown) result = SEND_EXIT_RESULT.RELAY_UNKNOWN;

                state = "exited";
                job = null;
                log(`sendExitGuard - 종료 작업 끝(${why}). result[${result}], failures[${failures.map((f) => f.name).join(",")}]`);

                if (result === SEND_EXIT_RESULT.DONE) runPending();
                else cancelPending(`종료 결과 ${result}`);

                resolveJob(result);
            })();

            return jobPromise;
        },

        /**
         * 20261030 브라우저 획득 요청 하나(+ 인계 전 로컬 처리)의 대기 ticket 을 연다 (R-238·R-240·R-262).
         * 종료 중/종료 뒤면 null — 새 획득을 시작하지 않는다.
         * @param {String} kind
         * @returns {{id:Number, kind:String, intent:Object}|null}
         */
        beginAcquire: (kind) => {
            if (state === "exiting" || state === "exited") {
                log(`sendExitGuard - 송출 종료 상태라 획득(${kind})을 시작하지 않는다.`);
                return null;
            }
            const id = ++acquireSeq;
            const intent = Object.freeze({ epoch, startState: state });
            const ticket = Object.freeze({ id, kind, intent });
            let resolve = null;
            const done = new Promise((r) => { resolve = r; });
            acquires.set(id, { ticket, done, resolve });
            return ticket;
        },

        /** 20261030 ticket 을 정확히 1회 해제한다. intent 는 해제 뒤에도 판정할 수 있다. */
        endAcquire: (ticket) => {
            if (!ticket) return;
            const entry = acquires.get(ticket.id);
            if (!entry) return;
            acquires.delete(ticket.id);
            entry.resolve();
        },

        /**
         * 20261030 원래 호출의 owner 세대가 아직 유효한지 매번 현재 상태로 판정한다 (R-262).
         * intent 가 없으면(기존 무인자 호출) true.
         */
        isIntentValid: (intent) => {
            if (intent === undefined || intent === null) return true;
            if (intent.epoch !== epoch) return false;
            if (state === "active") return true;
            return intent.startState === "idle" && state === "idle";
        },

        /** 20261030 다른 owner 가 열려 있는지(camera 콜백 판정용, R-262 §3.41.5). */
        isLaterOwnerActive: (intent) => !!intent && state === "active" && intent.epoch !== epoch,

        /** 20261030 호출 시점에 열린 ticket 이 모두 해제되면 끝난다. 타임아웃 없음. */
        waitAcquires: () => Promise.all(Array.from(acquires.values()).map((e) => e.done)).then(() => undefined),

        /** 시험용: 열린 ticket 수 */
        getAcquireCount: () => acquires.size,

        /** 정상 위치(sendUnpublish 뒤)와 finally 가 같은 정리를 공유한다. */
        finishOnce,

        /** relay close 가 실패했다. 결과를 relay-unknown 으로 남긴다(성공으로 바꾸지 않는다). */
        noteRelayUnknown: (token, err) => {
            if (token) token.relayUnknown = true;
            log(`sendExitGuard - relay 정리 결과 불명 ${err && err.message}`);
        },

        /**
         * 수신 입구 판정. 버려야 하면 true.
         * - 다른 liveSeq 의 handsup 송출 응답
         * - 종료 중/종료 후의 create/connect/produce/restart 응답
         * idle · liveSeq 없음 · 대상 아닌 kind/action 은 기존대로 둔다.
         */
        isStaleResponse: (kind, action, liveSeq) => {
            if (kind !== "handsup" || SEND_RESPONSE_ACTIONS.indexOf(action) < 0) return false;
            if (state === "idle") return false;
            const seq = toSeq(liveSeq);
            if (seq === null) return false;
            if ((state === "exiting" || state === "exited") && action !== "live_unpublish") return true;
            if (owner !== null && seq !== owner.liveSeq) return true;
            return false;
        }
    };
};
